import React from 'react';
import PropTypes from 'prop-types';
import { Line } from 'react-chartjs-2';
import DashboardStub from 'components/kea/DashboardStub';
import { analyticsChartTheme } from 'util/chartUtils';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import styles from './WidgetPerformance.module.scss';

export const calculateClickThroughRate = curve => curve.map((entry) => {
  const views = Number(entry.view) || 0;
  return views === 0 ? 0 : Math.round(((Number(entry.click) || 0) / views) * 100);
});

const WidgetPerformanceBlock = ({ widget }) => {
  const localizeMessage = useLocalizedMessage();
  const curve = widget.curve || [];
  const options = {
    tooltips: { mode: 'index', intersect: false },
    responsive: true,
    maintainAspectRatio: false,
    spanGaps: false,
    elements: { point: { radius: 0, hitRadius: 8 } },
    legend: { display: false },
    scales: {
      xAxes: [{
        ticks: { display: false, beginAtZero: true },
        gridLines: { display: false, tickMarkLength: 0 },
      }],
      yAxes: [{
        ticks: { beginAtZero: true, fontColor: analyticsChartTheme.muted },
        gridLines: { color: analyticsChartTheme.grid, drawBorder: false },
      }],
    },
  };

  const clickThroughRate = calculateClickThroughRate(curve);
  const chartData = {
    labels: curve.map((entry) => {
      const date = new Date(entry.date);
      return Number.isNaN(date.getTime()) ? entry.date : date.toISOString().slice(0, 10);
    }),
    datasets: [{
      backgroundColor: analyticsChartTheme.clickRateFill,
      borderColor: analyticsChartTheme.clickRate,
      borderWidth: 2,
      data: clickThroughRate,
      label: localizeMessage('recs.analytics.widgetsPerformance.ctr'),
      fill: 'start',
    }],
  };
  const counters = [
    ['recs.analytics.widgetsPerformance.shown', widget.counters?.show || 0],
    ['recs.analytics.widgetsPerformance.viewed', widget.counters?.view || 0],
    ['recs.analytics.widgetsPerformance.clicked', widget.counters?.click || 0],
  ];

  return (
    <article className={styles.widgetCard} data-testid="recommendation-widget-performance">
      <div className={styles.widgetHeader}>
        <div>
          <h3>{widget.data?.title || widget.id}</h3>
          {widget.data?.model && <span className={styles.modelBadge}>{widget.data.model}</span>}
        </div>
        <div className={styles.ctrValue}>
          <strong>{widget.stats?.clickRate || 0}%</strong>
          <span>{localizeMessage('recs.analytics.widgetsPerformance.ctr')}</span>
        </div>
      </div>
      <div className={styles.widgetBody}>
        <div className={styles.chartFrame}>
          {curve.length ? <Line data={chartData} options={options} /> : (
            <DashboardStub compact variant="line" messageId="recs.analytics.widgetsPerformance.emptyWidget" />
          )}
        </div>
        <dl className={styles.counterGrid}>
          {counters.map(([key, value]) => (
            <div key={key}>
              <dt>{localizeMessage(key)}</dt>
              <dd>{Number(value).toLocaleString()}</dd>
            </div>
          ))}
        </dl>
      </div>
    </article>
  );
};

WidgetPerformanceBlock.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.shape({ title: PropTypes.string, model: PropTypes.string }),
    curve: PropTypes.arrayOf(PropTypes.object),
    counters: PropTypes.shape({ show: PropTypes.number, view: PropTypes.number, click: PropTypes.number }),
    stats: PropTypes.shape({ clickRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  }).isRequired,
};

export default WidgetPerformanceBlock;
